import {
  AssetStructAddAction,
  AssetStructDeleteAction,
  AssetStructGetAction,
  AssetStructListAction,
  AssetStructUpdateAction,
  AssetStructUpdateActionArgs,
  KalturaAssetStruct,
  KalturaAssetStructArgs,
  KalturaAssetStructFilter,
  KalturaAssetStructFilterArgs,
  KalturaAssetStructListResponse,
  KalturaMeta,
} from '@kaltura-ott/ott-client-types/api/types';
import { forkJoin, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { kalturaClient } from '../kaltura-clent';
import { defaultMetaIDs } from '../metas';
import { KUX_FEATURE_NAME } from '../utils/constants';
import { filterByKUXPrefix } from '../utils/filters';

import { getCategoryVirtualAssetConfig } from './virtual-assets';

const getIdsOnly = (metas: (number | KalturaMeta)[]): (string | number | undefined)[] => {
  return (metas as []).map((meta: number | KalturaMeta) => {
    if (meta instanceof KalturaMeta) {
      return meta.id;
    } else if (!isNaN(meta)) {
      return meta;
    }
    console.warn("this meta wasn't processed", meta);
  });
};

/**
 * Will create category struct with given metas
 *
 * @param assetStruct: KalturaAssetStruct
 */
export const createCategoryStruct = (assetStruct: KalturaAssetStructArgs): Observable<KalturaAssetStruct> => {
  assetStruct.features = `${KUX_FEATURE_NAME}`;
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  return defaultMetaIDs().pipe(
    mergeMap((ids) => {
      const _m = assetStruct.metaIds?.split(',') || [];
      assetStruct.metaIds = [...ids, ..._m].join(',');
      const action = new AssetStructAddAction({ assetStruct: new KalturaAssetStruct(assetStruct) });
      return client.request(action);
    }),
  );
};

/**
 * Update category template - struct
 * Add metas to struct
 *
 * @param data:AssetStructUpdateActionArgs
 */
export const updateCategoryStruct = (data: AssetStructUpdateActionArgs) => {
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  const action = new AssetStructUpdateAction(data);
  return client.request(action);
};

/**
 *
 * @param structId
 */
export const getCategoryStruct = (structId: number): Observable<KalturaAssetStruct> => {
  const client = kalturaClient();
  const action = new AssetStructGetAction({ id: structId });
  return client.request(action);
};

export interface AddMetasToCategoryStructArgs {
  features?: string;
  structId: number;
  metas: (number | KalturaMeta)[];
}

/**
 * Will ADD metas into given struct
 * @param data
 */
export const appendMetasToCategoryStruct = (data: AddMetasToCategoryStructArgs) => {
  const idsToAdd = getIdsOnly(data.metas);

  return getCategoryStruct(data.structId).pipe(
    mergeMap((struct) => {
      const _data: AssetStructUpdateActionArgs = {
        id: data.structId,
        assetStruct: new KalturaAssetStruct({
          features: struct.features,
          metaIds: [...struct.metaIds.split(','), ...idsToAdd].join(','),
        }),
      };
      return updateCategoryStruct(_data);
    }),
  );
};

/**
 * Remove given metas/metaIds from struct
 * @param data
 */
export const removeMetasFromCategoryStruct = (data: AddMetasToCategoryStructArgs) => {
  const idsToRemove = getIdsOnly(data.metas);

  return getCategoryStruct(data.structId).pipe(
    mergeMap((struct) => {
      const metaIdsOnStruct = struct.metaIds.split(',');
      const metasToLeave = metaIdsOnStruct.filter((id) => !idsToRemove.includes(id));
      const _data: AssetStructUpdateActionArgs = {
        id: data.structId,
        assetStruct: new KalturaAssetStruct({
          features: struct.features,
          metaIds: [...metasToLeave].join(','),
        }),
      };
      return updateCategoryStruct(_data);
    }),
  );
};

/**
 * Will  remove all metas from struct and set with provided.
 * can be used to for:
 * 1) reorder metas on struct
 * 2) add new metas from scratch
 * @param data
 */
export const setMetasOnCategoryStruct = (data: AddMetasToCategoryStructArgs) => {
  const idsToAdd = getIdsOnly(data.metas);

  const _data: AssetStructUpdateActionArgs = {
    id: data.structId,
    assetStruct: new KalturaAssetStruct({
      metaIds: idsToAdd.join(','),
    }),
  };
  return updateCategoryStruct(_data);
};

export const getCategoryStructsList = (
  filter?: KalturaAssetStructFilterArgs,
): Observable<KalturaAssetStructListResponse> => {
  let requestFilter: KalturaAssetStructFilter = new KalturaAssetStructFilter({});
  if (filter) {
    requestFilter = new KalturaAssetStructFilter(filter);
  }
  const client = kalturaClient();
  const action = new AssetStructListAction(requestFilter);
  return forkJoin([client.request(action), getCategoryVirtualAssetConfig()]).pipe(
    map((responses) => {
      const structsData = filterByKUXPrefix(responses[0]);
      const virtualAssetsConfig = responses[1];
      if (virtualAssetsConfig) {
        // @ts-expect-error TODO Fix
        structsData.objects.forEach((struct) => {
          struct['typeExists'] = virtualAssetsConfig.extendedTypes[struct.systemName] ? true : false;
        });
      }
      return structsData;
    }),
  );
};

/**
 * Remove Category Struct
 * @category Category
 * @param id
 */
export const deleteCategoryStruct = (id: number) => {
  const client = kalturaClient();
  const action = new AssetStructDeleteAction({ id });
  return client.request(action);
};
